import React, {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {authAPI} from '../../../services/AuthService'
import {useIntl} from 'react-intl'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = (f: any) =>
  Yup.object().shape({
    email: Yup.string()
      .email(f({id: 'FORMIK.ERROR.WRONGEMAILFORMAT'}))
      .min(3, f({id: 'FORMIK.ERROR.MIN'}))
      .max(50, f({id: 'FORMIK.ERROR.MAX'}))
      .required(f({id: 'EMAIL.LABEL'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
  })

export function ForgotPassword() {
  const intl = useIntl()
  const [isClickSubmit, setIsClickSubmit] = useState<boolean>(false)
  const navigate = useNavigate()
  const [forgotPassword, {isLoading: loading, isError, isSuccess}] = authAPI.useForgotPasswordMutation()

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema(intl.formatMessage),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setIsClickSubmit(true)
      await forgotPassword({email: values.email})
        .unwrap()
        .then((response) => {
          if (response.status === 'success') {
            setStatus(intl.formatMessage({id: 'PASSWORD.RECOVERY'}) + values.email)
          }
        })
        .catch((err) => {
          setStatus(err.data.detail)
        })
        .finally(() => {
          setTimeout(() => {
            navigate('/login', {replace: true})
          }, 2000)
        })
    },
  })

  return (
    <>
      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_login_password_reset_form'
        onSubmit={formik.handleSubmit}
      >
        <div className='text-center mb-10'>
          <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'PASSWORD.FORGOT'})}?</h1>
          <div className='text-gray-400 fw-bold fs-4'>{intl.formatMessage({id: 'PASSWORD.FORGOT.DESCRIPTION'})}</div>
        </div>
        {formik.status && (
          <>
            {isError && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{intl.formatMessage({id: 'MESSAGE.ERROR'})}</div>
              </div>
            )}
            {!isError && formik.status !== 'Bad Request' && (
              <div className='mb-10 bg-light-info p-8 rounded'>
                <div className='text-info'>{formik.status}</div>
              </div>
            )}
          </>
        )}
        <div className='fv-row mb-10'>
          <label className='form-label fw-bolder text-gray-900 fs-6'>{intl.formatMessage({id: 'EMAIL.LABEL'})}</label>
          <input
            type='email'
            placeholder={intl.formatMessage({id: 'EMAIL.PLACEHOLDER'})}
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
          <button
            type='submit'
            id='kt_password_reset_submit'
            className='btn btn-lg btn-primary fw-bolder me-4'
            disabled={
              formik.isSubmitting ||
              !formik.isValid ||
              isSuccess ||
              formik.getFieldProps('email').value === '' ||
              isClickSubmit
            }
            // onClick={() => setIsClickSubmit(true)}
          >
            {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'BUTTON.SUBMIT'})}</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {intl.formatMessage({id: 'WAIT'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/auth/login'>
            <button
              type='button'
              id='kt_login_password_reset_form_cancel_button'
              className='btn btn-lg btn-light-primary fw-bolder'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {intl.formatMessage({id: 'BUTTON.CANCEL'})}
            </button>
          </Link>{' '}
        </div>
      </form>
    </>
  )
}
